import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import { EVENT_TYPES, CONTACT } from "../../lib/content";
import { Kicker, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", event_type: "", event_date: "", guests: "", message: "" };
const inputCls = "w-full bg-transparent border-b border-[var(--line)] py-3 text-base text-[var(--ink)] placeholder:text-[#6b7482] focus:outline-none focus:border-[var(--cyan)] transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--muted)]">{label}</span>
    {children}
  </label>
);

export const Inquiry = ({ dishes = [], onToggle, onClear }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.event_type) { toast.error("Vyberte prosím typ akce."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { ...f, dishes, guests: f.guests ? Number(f.guests) : null });
      setSent(true);
      setF(EMPTY);
      onClear?.();
      toast.success("Poptávka odeslána. Ozveme se co nejdříve.");
    } catch (err) {
      toast.error("Odeslání se nezdařilo. Zkontrolujte prosím údaje.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="poptavka" data-testid="inquiry-section" className="py-24 md:py-36 relative overflow-hidden grid-bg">
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#fcd34d] opacity-30 blur-[120px]" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14 relative">
        <Reveal className="lg:col-span-5">
          <Kicker>Poptávka</Kicker>
          <h2 className="mt-6 font-display text-4xl md:text-6xl font-semibold uppercase leading-[0.95]">Řekněte nám<br /><span className="text-[var(--cyan)]">jedno slovo.</span></h2>
          <p className="mt-6 text-[var(--muted)] text-base md:text-lg leading-relaxed max-w-md">Popište svou akci a my připravíme menu i cenu na míru. Nezávazně a zdarma.</p>
          <div className="mt-10 space-y-3 font-mono text-sm">
            <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="block hover:text-[var(--cyan)]"><span className="text-[var(--muted)]">E-mail /</span> {CONTACT.email}</a>
            <a data-testid="contact-phone" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="block hover:text-[var(--cyan)]"><span className="text-[var(--muted)]">Telefon /</span> {CONTACT.phone}</a>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          <form data-testid="inquiry-form" onSubmit={submit} className="hud-frame border border-[var(--line)] bg-white/85 backdrop-blur-xl shadow-[0_30px_80px_-40px_rgba(8,92,120,.35)] p-6 md:p-10 space-y-8">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--muted)]">Typ akce</span>
              <div className="mt-4 flex flex-wrap gap-2">
                {EVENT_TYPES.map((t) => (
                  <button type="button" key={t} data-testid={`event-type-${t}`} onClick={() => setF({ ...f, event_type: t })}
                    className={`rounded-full px-4 py-2 text-sm border transition-colors ${f.event_type === t ? "bg-[var(--cyan)] text-white border-[var(--cyan)]" : "border-[var(--line)] text-[var(--ink)] hover:border-[var(--cyan)]"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div data-testid="selected-dishes">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--muted)]">Vybraná jídla ({dishes.length})</span>
              {dishes.length === 0 ? (
                <p className="mt-3 text-sm text-[var(--muted)]">Zatím nic. Jídla přidáte klepnutím v sekci Nabídka.</p>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  {dishes.map((d) => (
                    <button type="button" key={d} onClick={() => onToggle?.(d)} data-testid={`selected-dish-${d}`}
                      className="rounded-full bg-[rgba(46,91,37,0.08)] border border-[rgba(46,91,37,0.3)] text-[var(--cyan)] pl-4 pr-2 py-1.5 text-sm flex items-center gap-2 hover:bg-[rgba(46,91,37,0.16)]">
                      {d}<X size={14} />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              <Field label="Jméno *"><input required minLength={2} data-testid="input-name" className={inputCls} value={f.name} onChange={set("name")} placeholder="Jana Nováková" /></Field>
              <Field label="E-mail *"><input required type="email" data-testid="input-email" className={inputCls} value={f.email} onChange={set("email")} placeholder="jana@email.cz" /></Field>
              <Field label="Telefon"><input data-testid="input-phone" className={inputCls} value={f.phone} onChange={set("phone")} placeholder="+420 …" /></Field>
              <Field label="Datum akce"><input type="date" data-testid="input-date" className={`${inputCls} [color-scheme:light]`} value={f.event_date} onChange={set("event_date")} /></Field>
              <Field label="Počet hostů"><input type="number" min={1} max={5000} data-testid="input-guests" className={inputCls} value={f.guests} onChange={set("guests")} placeholder="např. 80" /></Field>
            </div>
            <Field label="Vaše představa"><textarea rows={4} data-testid="input-message" className={`${inputCls} resize-none`} value={f.message} onChange={set("message")} placeholder="Místo, styl, dietní omezení…" /></Field>
            <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={busy} data-testid="inquiry-submit"
              className="w-full sm:w-auto rounded-full bg-[var(--cyan)] text-white px-10 py-4 font-semibold flex items-center justify-center gap-3 hover:bg-[var(--ink)] transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={18} /> : sent ? <Check size={18} /> : null}
              {sent ? "Odesláno — poslat další" : "Odeslat poptávku"}
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
